/**
 * Settings Page - Comprehensive workspace and system settings
 * Task 31: Added Tenant Configuration section
 */

import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card } from '../UI/Card';
import { Button } from '../UI/Button';
import { Input } from '../UI/Input';
import { Badge } from '../UI/Badge';
import { useWorkspace } from '../../hooks/useWorkspace';
import { listTenants, listProjects } from '../../api';
import { getCacheStats, clearCache } from '../../api/cache';
import { listWebhooks, deleteWebhook } from '../../api/webhooks';
import { 
  listPolicyPacks, 
  getPolicyPack, 
  deletePolicyPack, 
  createPolicyPack,
  validatePolicyPack,
} from '../../api/policy';
import { 
  Settings as SettingsIcon, 
  Database, 
  Trash2, 
  RefreshCw,
  Webhook,
  BarChart,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  FileCode,
  HardDrive,
  Link,
  Shield,
  FileText,
  Edit3,
  Save,
  X,
  Download,
  FileSearch,
  Activity,
  Cloud,
} from 'lucide-react';
import { DocumentGapsPanel } from '../Analytics/DocumentGapsPanel';
import { QAObservabilityPanel } from './QAObservabilityPanel';
import { RetrievalDashboard } from './RetrievalDashboard';
import { TenantConfigEditor } from '../Tenants/TenantConfigEditor';
import { TenantConfigValidator } from '../Tenants/TenantConfigValidator';
import { TenantConfigImportExport } from '../Tenants/TenantConfigImportExport';
import { TenantConfigStatus } from '../Tenants/TenantConfigStatus';
import { useThemeStore } from '../../stores/theme';
import { Moon, Sun, Monitor, Bell } from 'lucide-react';
import { getApiBaseUrl } from '../../utils/env';
import { PolicyPackViewer } from '../Policy/PolicyPackViewer';
import { NotificationPreferences } from '../Notifications/NotificationPreferences';

export function Settings() {
  const { projectId, tenantId, tenant, project } = useWorkspace();
  const queryClient = useQueryClient();
  const { theme, setTheme } = useThemeStore();
  
  // Read tab from URL, default to workspace
  const getInitialTab = (): 'workspace' | 'tenant-config' | 'cache' | 'webhooks' | 'policy' | 'analytics' | 'qa' | 'retrieval' | 'system' | 'notifications' => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    if (tab && ['workspace', 'tenant-config', 'cache', 'webhooks', 'policy', 'analytics', 'qa', 'retrieval', 'system', 'notifications'].includes(tab)) {
      return tab as any;
    }
    return 'workspace';
  };
  
  const [activeTab, setActiveTab] = useState<'workspace' | 'tenant-config' | 'cache' | 'webhooks' | 'policy' | 'analytics' | 'qa' | 'retrieval' | 'system' | 'notifications'>(getInitialTab());
  const [tenantConfigSection, setTenantConfigSection] = useState<'editor' | 'validate' | 'import-export'>('editor');
  const [showUploadPolicy, setShowUploadPolicy] = useState(false);
  const [selectedPolicyFile, setSelectedPolicyFile] = useState<File | null>(null);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [expandedPolicyId, setExpandedPolicyId] = useState<string | null>(null);
  const [editingPolicyId, setEditingPolicyId] = useState<string | null>(null);
  const [editedPolicyData, setEditedPolicyData] = useState<any>(null);
  
  // Update URL when tab changes
  const handleTabChange = (tab: typeof activeTab) => {
    setActiveTab(tab);
    const url = new URL(window.location.href);
    url.searchParams.set('tab', tab);
    window.history.pushState({}, '', url.toString());
  };

  // Fetch full policy pack details when one is expanded
  const { data: expandedPolicyDetails } = useQuery({
    queryKey: ['policy-pack-details', expandedPolicyId],
    queryFn: () => getPolicyPack(expandedPolicyId!),
    enabled: !!expandedPolicyId,
  });

  const { data: cacheStats } = useQuery({
    queryKey: ['cache-stats', projectId],
    queryFn: () => getCacheStats(projectId!),
    enabled: !!projectId,
  });

  const { data: webhooks } = useQuery({
    queryKey: ['webhooks', projectId],
    queryFn: () => listWebhooks(projectId!),
    enabled: !!projectId,
  });

  const clearCacheMutation = useMutation({
    mutationFn: () => clearCache(projectId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cache-stats'] });
      alert('Cache cleared successfully');
    },
  });

  const deleteWebhookMutation = useMutation({
    mutationFn: (webhookId: string) => deleteWebhook(webhookId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['webhooks'] });
    },
  });

  const { data: policyPacks, refetch: refetchPolicies, isLoading: loadingPolicies } = useQuery({
    queryKey: ['policy-packs', tenantId],
    queryFn: async () => {
      console.log('[Settings] Loading policy packs for tenant:', tenantId);
      // Load ALL policy packs (backend filters by tenant if needed)
      const packs = await listPolicyPacks();
      console.log('[Settings] Policy packs loaded:', packs);
      console.log('[Settings] Raw response:', packs);
      return packs;
    },
    refetchInterval: false, // Disable auto-refresh - only refetch on manual action or query invalidation
    staleTime: 60000, // Consider data fresh for 60 seconds
  });

  // Load all projects to count connections
  const { data: allProjects } = useQuery({
    queryKey: ['all-projects'],
    queryFn: () => listProjects(),
    enabled: true,
  });

  // Load all tenants for name resolution
  const { data: allTenants } = useQuery({
    queryKey: ['all-tenants'],
    queryFn: () => listTenants(),
    enabled: true,
  });

  const deletePolicyMutation = useMutation({
    mutationFn: (policyPackId: string) => deletePolicyPack(policyPackId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy-packs'] });
    },
  });

  const createPolicyMutation = useMutation({
    mutationFn: (file: File) => createPolicyPack(file),
    onSuccess: (result) => {
      console.log('[Settings] Policy pack created:', result);
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: ['policy-packs'] });
      refetchPolicies();
      setShowUploadPolicy(false);
      setSelectedPolicyFile(null);
      setValidationResult(null);
      alert('Policy pack uploaded successfully!');
    },
    onError: (error: any) => {
      console.error('[Settings] Failed to create policy pack:', error);
      alert(`Failed to upload: ${error.message}`);
    },
  });

  const validatePolicyMutation = useMutation({
    mutationFn: (file: File) => validatePolicyPack(file),
    onSuccess: (result) => {
      setValidationResult(result);
    },
  });

  const handlePolicyFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedPolicyFile(file);
      await validatePolicyMutation.mutateAsync(file);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <SettingsIcon className="h-8 w-8 text-blue-600" />
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Settings</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-1">Manage workspace and system configuration</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 dark:border-slate-700">
        <div className="flex gap-4">
          {[
            { id: 'workspace', label: 'Workspace', icon: Database },
            { id: 'tenant-config', label: 'Tenant Config', icon: Cloud },
            { id: 'policy', label: 'Policy Packs', icon: SettingsIcon },
            { id: 'cache', label: 'Cache', icon: BarChart },
            { id: 'webhooks', label: 'Webhooks', icon: Webhook },
            { id: 'analytics', label: 'Document Gaps', icon: FileSearch },
            { id: 'qa', label: 'QA & Observability', icon: Activity },
            { id: 'retrieval', label: 'Retrieval & Search', icon: FileSearch },
            { id: 'notifications', label: 'Notifications', icon: Bell },
            { id: 'system', label: 'System & Appearance', icon: SettingsIcon },
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => handleTabChange(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors ${
                  activeTab === tab.id
                    ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400 font-medium'
                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Workspace Tab */}
      {activeTab === 'workspace' && (
        <Card title="Workspace Information">
          <div className="space-y-6">
            {/* Tenant Info */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Current Tenant</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Name</div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100">
                    {tenant?.name || 'Not selected'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Tenant ID</div>
                  <div className="font-mono text-sm text-gray-700">
                    {tenantId || 'Not selected'}
                  </div>
                </div>
              </div>
            </div>

            {/* Project Info */}
            <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
              <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Current Project</div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-gray-500 mb-1">Name</div>
                  <div className="font-semibold text-gray-900 dark:text-gray-100">
                    {project?.name || 'Not selected'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-gray-500 mb-1">Project ID</div>
                  <div className="font-mono text-sm text-gray-700">
                    {projectId || 'Not selected'}
                  </div>
                </div>
                {project?.description && (
                  <div className="col-span-2">
                    <div className="text-xs text-gray-500 mb-1">Description</div>
                    <div className="text-sm text-gray-700">
                      {project.description}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Tenant Config Tab */}
      {activeTab === 'tenant-config' && tenantId && tenant && (
        <div className="space-y-6">
          {/* Sub-navigation */}
          <div className="flex items-center gap-4 border-b border-gray-200 pb-3">
            <button
              onClick={() => setTenantConfigSection('editor')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                tenantConfigSection === 'editor'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Configuration Editor
            </button>
            <button
              onClick={() => setTenantConfigSection('validate')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                tenantConfigSection === 'validate'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Validate Connections
            </button>
            <button
              onClick={() => setTenantConfigSection('import-export')}
              className={`px-4 py-2 rounded-lg transition-colors ${
                tenantConfigSection === 'import-export'
                  ? 'bg-blue-100 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              Import/Export
            </button>
          </div>

          {tenantConfigSection === 'editor' && (
            <TenantConfigEditor 
              tenantId={tenantId} 
              tenantName={tenant.name}
              onSaved={() => {
                queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantId] });
              }}
            />
          )}

          {tenantConfigSection === 'validate' && (
            <TenantConfigValidator 
              tenantId={tenantId} 
              tenantName={tenant.name} 
            />
          )}

          {tenantConfigSection === 'import-export' && (
            <TenantConfigImportExport 
              tenantId={tenantId} 
              tenantName={tenant.name}
              onImported={() => {
                queryClient.invalidateQueries({ queryKey: ['tenant-config', tenantId] });
              }}
            />
          )}
        </div>
      )}

      {activeTab === 'tenant-config' && !tenantId && (
        <Card className="p-8 text-center">
          <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Please select a tenant first to configure its cloud resources.</p>
        </Card>
      )}

      {/* Cache Tab */}
      {activeTab === 'cache' && (
        <div className="space-y-6">
          <Card title="Query Cache Statistics">
            {cacheStats ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{cacheStats.total_entries}</div>
                    <div className="text-sm text-gray-600">Total Entries</div>
                  </div>
                  <div className="bg-green-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{cacheStats.active_entries}</div>
                    <div className="text-sm text-gray-600">Active</div>
                  </div>
                  <div className="bg-orange-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-orange-600">{cacheStats.expired_entries}</div>
                    <div className="text-sm text-gray-600">Expired</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <div className="text-2xl font-bold text-purple-600">
                      {(cacheStats.cache_hit_rate * 100).toFixed(1)}%
                    </div>
                    <div className="text-sm text-gray-600">Hit Rate</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <div className="text-gray-500">Estimated Size</div>
                    <div className="font-medium">{cacheStats.estimated_size_mb.toFixed(2)} MB</div>
                  </div>
                  <div>
                    <div className="text-gray-500">TTL</div>
                    <div className="font-medium">{cacheStats.ttl_hours} hours</div>
                  </div>
                  <div>
                    <div className="text-gray-500">Oldest Entry</div>
                    <div className="font-medium">
                      {cacheStats.oldest_entry ? new Date(cacheStats.oldest_entry).toLocaleString() : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <div className="text-gray-500">Newest Entry</div>
                    <div className="font-medium">
                      {cacheStats.newest_entry ? new Date(cacheStats.newest_entry).toLocaleString() : 'N/A'}
                    </div>
                  </div>
                </div>

                <div className="pt-4 border-t border-gray-200">
                  <Button
                    variant="outline"
                    onClick={() => {
                      if (window.confirm('Clear all cached queries? This will slow down the next requests.')) {
                        clearCacheMutation.mutate();
                      }
                    }}
                    disabled={clearCacheMutation.isPending}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                    Clear Cache
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-center py-8 text-gray-500">No cache statistics available</p>
            )}
          </Card>
        </div>
      )}

      {/* Policy Packs Tab */}
      {activeTab === 'policy' && (
        <div className="space-y-6">
          <Card title="Policy Packs" actions={
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => refetchPolicies()}>
                <RefreshCw className="h-4 w-4" />
                Refresh
              </Button>
              <Button size="sm" onClick={() => setShowUploadPolicy(true)}>
                Upload Policy Pack
              </Button>
            </div>
          }>
            {loadingPolicies ? (
              <div className="text-center py-8 text-gray-500">Loading policy packs...</div>
            ) : policyPacks && policyPacks.length > 0 ? (
              <div className="space-y-3">
                {policyPacks.map((pack: any) => {
                  console.log('[Settings] Rendering policy pack:', pack);
                  
                  // Count projects using this policy pack
                  const connectedProjects = allProjects?.filter((p: any) => p.policy_pack_id === pack.id) || [];
                  const projectCount = connectedProjects.length;
                  
                  // Get tenant name
                  const packTenant = pack.tenant_id ? allTenants?.find((t: any) => t.id === pack.tenant_id) : null;
                  
                  // Use full details if expanded, otherwise use summary
                  const policyData = (expandedPolicyId === pack.id && expandedPolicyDetails) 
                    ? (expandedPolicyDetails.rules || expandedPolicyDetails.policy_data || {})
                    : (pack.policy_data || pack.rules || {});
                  
                  const fileSize = JSON.stringify(policyData).length;
                  const fileSizeKB = (fileSize / 1024).toFixed(1);
                  
                  // Count different policy components (handle both object and array formats)
                  const prohibitedCount = policyData.prohibited_content 
                    ? (Array.isArray(policyData.prohibited_content) 
                        ? policyData.prohibited_content.length 
                        : Object.keys(policyData.prohibited_content).length)
                    : 0;
                  
                  const requiredEvidenceCount = policyData.evidence_requirements?.require_evidence_for?.length || 0;
                  const taxonomyOverrides = policyData.taxonomy_overrides ? Object.keys(policyData.taxonomy_overrides).length : 0;
                  // report_templates is an object (dict), not an array
                  const reportTemplates = policyData.report_templates 
                    ? (Array.isArray(policyData.report_templates) 
                        ? policyData.report_templates.length 
                        : Object.keys(policyData.report_templates).length)
                    : 0;
                  const querySamples = Array.isArray(policyData.query_samples) ? policyData.query_samples.length : 0;
                  const requiredValidations = policyData.required_validations?.length || 0;
                  const complianceRules = policyData.compliance_rules?.length || 0;
                  
                  const isExpanded = expandedPolicyId === pack.id;
                  const isEditing = editingPolicyId === pack.id;
                  
                  return (
                    <div key={pack.id} className="border-2 border-gray-200 rounded-lg overflow-hidden hover:border-blue-300 transition-colors">
                      {/* Header - Always Visible */}
                      <div className="p-4 bg-white dark:bg-gray-800">
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <button
                                onClick={() => setExpandedPolicyId(isExpanded ? null : pack.id)}
                                className="text-gray-600 hover:text-gray-900 transition-colors"
                              >
                                {isExpanded ? (
                                  <ChevronDown className="h-5 w-5" />
                                ) : (
                                  <ChevronRight className="h-5 w-5" />
                                )}
                              </button>
                              <Shield className="h-5 w-5 text-blue-600" />
                              <h3 className="font-semibold text-gray-900 dark:text-gray-100 text-lg">{pack.name}</h3>
                              <Badge variant="success">v{pack.version}</Badge>
                              {projectCount > 0 && (
                                <Badge variant="default" className="bg-green-100 text-green-800">
                                  <Link className="h-3 w-3 mr-1" />
                                  {projectCount} project{projectCount !== 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-600 mb-3 ml-9">{pack.description}</p>
                            
                            {/* Quick Stats Row */}
                            <div className="grid grid-cols-4 gap-3 text-xs ml-9">
                              <div className="bg-gray-50 dark:bg-gray-700 px-3 py-2 rounded text-gray-900 dark:text-gray-100">
                                <div className="text-gray-500 mb-0.5">Tenant</div>
                                <div className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {packTenant?.name || (pack.tenant_id ? pack.tenant_id.substring(0, 8) + '...' : 'Global')}
                                </div>
                              </div>
                              <div className="bg-blue-50 px-3 py-2 rounded">
                                <div className="text-gray-500 mb-0.5">Size</div>
                                <div className="font-medium text-blue-900">{fileSizeKB} KB</div>
                              </div>
                              <div className="bg-purple-50 px-3 py-2 rounded">
                                <div className="text-gray-500 mb-0.5">Rules</div>
                                <div className="font-medium text-purple-900">{prohibitedCount + requiredEvidenceCount}</div>
                              </div>
                              <div className="bg-green-50 px-3 py-2 rounded">
                                <div className="text-gray-500 mb-0.5">Created</div>
                                <div className="font-medium text-green-900">
                                  {new Date(pack.created_at).toLocaleDateString()}
                                </div>
                              </div>
                            </div>
                          </div>
                          <div className="flex gap-2">
                            {isEditing ? (
                              <>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setEditingPolicyId(null);
                                    setEditedPolicyData(null);
                                  }}
                                >
                                  <X className="h-4 w-4" />
                                  Cancel
                                </Button>
                                <Button
                                  size="sm"
                                  onClick={() => {
                                    alert('Policy pack update API not yet implemented. Would save changes to backend.');
                                    setEditingPolicyId(null);
                                  }}
                                >
                                  <Save className="h-4 w-4" />
                                  Save
                                </Button>
                              </>
                            ) : (
                              <>
                                {isExpanded && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setEditingPolicyId(pack.id);
                                      setEditedPolicyData(JSON.stringify(policyData, null, 2));
                                    }}
                                  >
                                    <Edit3 className="h-4 w-4" />
                                    Edit
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    // Download actual policy pack content
                                    const fullData = expandedPolicyDetails || pack;
                                    const contentToDownload = {
                                      name: fullData.name,
                                      version: fullData.version,
                                      description: fullData.description,
                                      ...(fullData.rules || fullData.policy_data || {}),
                                    };
                                    const yaml = JSON.stringify(contentToDownload, null, 2);
                                    const blob = new Blob([yaml], { type: 'application/json' });
                                    const url = window.URL.createObjectURL(blob);
                                    const a = document.createElement('a');
                                    a.href = url;
                                    a.download = `${pack.name.replace(/[^a-z0-9]/gi, '_')}_v${pack.version}.json`;
                                    document.body.appendChild(a);
                                    a.click();
                                    document.body.removeChild(a);
                                    window.URL.revokeObjectURL(url);
                                  }}
                                >
                                  <Download className="h-4 w-4" />
                                  Download
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    if (projectCount > 0) {
                                      alert(`Cannot delete: This policy pack is connected to ${projectCount} project(s):\n${connectedProjects.map((p: any) => `- ${p.name}`).join('\n')}`);
                                      return;
                                    }
                                    if (window.confirm(`Delete policy pack "${pack.name}"?`)) {
                                      deletePolicyMutation.mutate(pack.id);
                                    }
                                  }}
                                  className="text-red-600"
                                  disabled={deletePolicyMutation.isPending || projectCount > 0}
                                  title={projectCount > 0 ? `Connected to ${projectCount} project(s)` : 'Delete policy pack'}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-4">
                          {isEditing ? (
                            /* Editing Mode - Use PolicyPackViewer */
                            <PolicyPackViewer
                              policyPack={{
                                id: pack.id,
                                name: pack.name,
                                version: pack.version,
                                description: pack.description || '',
                                rules: expandedPolicyDetails?.rules || expandedPolicyDetails?.policy_data || policyData,
                                policy_data: expandedPolicyDetails?.policy_data || expandedPolicyDetails?.rules || policyData,
                                disclosure_text: expandedPolicyDetails?.disclosure_text || policyData.disclosure?.text,
                              }}
                              editable={true}
                              onSave={async (updatedPolicyPack) => {
                                try {
                                  // TODO: Implement API call to save policy pack
                                  alert('Policy pack update API not yet implemented. Would save changes to backend.');
                                  setEditingPolicyId(null);
                                  setEditedPolicyData(null);
                                  // Refetch to get updated data
                                  if (expandedPolicyId === pack.id) {
                                    // Refetch expanded details
                                  }
                                } catch (error) {
                                  console.error('Failed to save policy pack:', error);
                                  alert('Failed to save policy pack. Please try again.');
                                }
                              }}
                            />
                          ) : (
                            /* Viewing Mode - Use PolicyPackViewer in read-only mode */
                            <PolicyPackViewer
                              policyPack={{
                                id: pack.id,
                                name: pack.name,
                                version: pack.version,
                                description: pack.description || '',
                                rules: expandedPolicyDetails?.rules || expandedPolicyDetails?.policy_data || policyData,
                                policy_data: expandedPolicyDetails?.policy_data || expandedPolicyDetails?.rules || policyData,
                                disclosure_text: expandedPolicyDetails?.disclosure_text || policyData.disclosure?.text,
                              }}
                              editable={false}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12">
                <AlertCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-500 mb-2">No policy packs found</p>
                <p className="text-sm text-gray-400 mb-4">
                  Tenant: {tenantId || 'Not selected'}
                </p>
                <Button onClick={() => setShowUploadPolicy(true)}>
                  Upload Your First Policy Pack
                </Button>
              </div>
            )}
          </Card>

          {/* Upload Policy Pack Modal */}
          {showUploadPolicy && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowUploadPolicy(false)}>
              <div className="max-w-2xl w-full mx-4" onClick={(e) => e.stopPropagation()}>
                <Card>
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold">Upload Policy Pack</h3>
                      <button
                        onClick={() => setShowUploadPolicy(false)}
                        className="text-gray-400 hover:text-gray-600"
                      >
                        ✕
                      </button>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select YAML File
                      </label>
                      <input
                        type="file"
                        accept=".yaml,.yml"
                        onChange={handlePolicyFileSelect}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                      />
                    </div>

                    {validationResult && (
                      <div className={`border rounded-lg p-4 ${
                        validationResult.valid
                          ? 'bg-green-50 border-green-200'
                          : 'bg-red-50 border-red-200'
                      }`}>
                        <div className="flex items-center gap-2 mb-2">
                          {validationResult.valid ? (
                            <>
                              <CheckCircle className="h-5 w-5 text-green-600" />
                              <span className="font-medium text-green-900">Valid Policy Pack</span>
                            </>
                          ) : (
                            <>
                              <AlertCircle className="h-5 w-5 text-red-600" />
                              <span className="font-medium text-red-900">Validation Errors</span>
                            </>
                          )}
                        </div>
                        {validationResult.errors?.map((err: any, idx: number) => (
                          <div key={idx} className="text-sm text-red-700 mt-1">
                            • {err.path}: {err.message}
                          </div>
                        ))}
                        {validationResult.warnings?.map((warn: string, idx: number) => (
                          <div key={idx} className="text-sm text-yellow-700 mt-1">
                            ⚠ {warn}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => setShowUploadPolicy(false)}>
                        Cancel
                      </Button>
                      <Button
                        onClick={() => {
                          if (selectedPolicyFile) {
                            createPolicyMutation.mutate(selectedPolicyFile);
                          }
                        }}
                        disabled={!selectedPolicyFile || !validationResult?.valid || createPolicyMutation.isPending}
                      >
                        {createPolicyMutation.isPending ? 'Creating...' : 'Create Policy Pack'}
                      </Button>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Webhooks Tab */}
      {activeTab === 'webhooks' && (
        <Card title="Webhooks">
          {webhooks && webhooks.length > 0 ? (
            <div className="space-y-3">
              {webhooks.map((webhook: any) => {
                console.log('[Settings] Webhook data:', webhook);
                return (
                  <div key={webhook.id || webhook._id || Math.random()} className="border border-gray-200 rounded-lg p-4 flex items-start justify-between">
                    <div className="flex-1">
                      <div className="font-medium text-gray-900 dark:text-gray-100 mb-1">{webhook.callback_url}</div>
                      <div className="flex items-center gap-2 text-sm">
                        <Badge variant={webhook.status === 'active' ? 'success' : 'default'}>
                          {webhook.status}
                        </Badge>
                        <span className="text-gray-500">
                          Events: {Array.isArray(webhook.event_types) ? webhook.event_types.join(', ') : 'N/A'}
                        </span>
                      </div>
                      <div className="text-xs text-gray-400 font-mono mt-1">
                        ID: {webhook.id || 'undefined'}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        if (!webhook.id) {
                          alert('Cannot delete: webhook ID is undefined');
                          return;
                        }
                        if (window.confirm('Delete this webhook?')) {
                          deleteWebhookMutation.mutate(webhook.id);
                        }
                      }}
                      className="text-red-600"
                      disabled={!webhook.id}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-center py-8 text-gray-500">No webhooks configured</p>
          )}
        </Card>
      )}

      {/* Analytics Tab - Document Gaps */}
      {activeTab === 'analytics' && projectId && (
        <div className="space-y-6">
          <DocumentGapsPanel 
            projectId={projectId} 
            onUploadClick={(docType) => {
              console.log('Upload requested for:', docType);
              alert(`Upload feature coming soon for: ${docType}`);
            }}
          />
        </div>
      )}

      {/* QA & Observability Tab */}
      {activeTab === 'qa' && projectId && (
        <QAObservabilityPanel projectId={projectId} />
      )}

      {/* Retrieval & Hybrid Search Tab */}
      {activeTab === 'retrieval' && (
        <RetrievalDashboard />
      )}

      {/* Notifications Tab */}
      {activeTab === 'notifications' && (
        <div className="space-y-6">
          <NotificationPreferences projectId={projectId} />
        </div>
      )}

      {/* System Tab */}
      {activeTab === 'system' && (
        <div className="space-y-6">
          <Card title="System Information">
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-text-secondary">TraceKit Version</div>
                  <div className="font-medium text-text-primary">v1.0.0</div>
                </div>
                <div>
                  <div className="text-text-secondary">API Base URL</div>
                  <div className="font-medium text-text-primary break-all">{getApiBaseUrl()}</div>
                </div>
              </div>
            </div>
          </Card>

          <Card title="Appearance">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  Theme
                </label>
                <div className="grid grid-cols-3 gap-3">
                  <button
                    onClick={() => {
                      setTheme('light');
                      // Force immediate application
                      setTimeout(() => {
                        const root = document.documentElement;
                        root.classList.remove('dark');
                        console.log('[Settings] Forced light mode, dark class removed:', !root.classList.contains('dark'));
                      }, 10);
                    }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      theme === 'light'
                        ? 'border-blue-600 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                    }`}
                  >
                    <Sun className={`h-6 w-6 ${theme === 'light' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                    <span className={`text-sm font-medium ${theme === 'light' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      Light
                    </span>
                  </button>
                  
                  <button
                    onClick={() => {
                      setTheme('dark');
                      // Force immediate application
                      setTimeout(() => {
                        const root = document.documentElement;
                        root.classList.add('dark');
                        console.log('[Settings] Forced dark mode, dark class added:', root.classList.contains('dark'));
                      }, 10);
                    }}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      theme === 'dark'
                        ? 'border-blue-600 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                    }`}
                  >
                    <Moon className={`h-6 w-6 ${theme === 'dark' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                    <span className={`text-sm font-medium ${theme === 'dark' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      Dark
                    </span>
                  </button>
                  
                  <button
                    onClick={() => setTheme('system')}
                    className={`flex flex-col items-center gap-2 p-4 rounded-lg border-2 transition-all ${
                      theme === 'system'
                        ? 'border-blue-600 dark:border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-slate-700 hover:border-gray-300 dark:hover:border-slate-600'
                    }`}
                  >
                    <Monitor className={`h-6 w-6 ${theme === 'system' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                    <span className={`text-sm font-medium ${theme === 'system' ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400'}`}>
                      System
                    </span>
                  </button>
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                  {theme === 'system' 
                    ? 'Theme follows your browser/system preference'
                    : theme === 'dark'
                    ? 'Dark mode is enabled'
                    : 'Light mode is enabled'}
                </p>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
